import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface SubscriptionRetentionCardProps {
  /** ISO timestamp of `subscriptions.current_period_end`. Can be historically NULL. */
  currentPeriodEnd: string | null;
  onManage: () => void;
  loading: boolean;
}

/**
 * Retention module shown in the Settings subscription tab once the user has
 * cancelled (cancel_at_period_end = true, status not yet 'canceled'). Replaces
 * the upgrade upsell CTA — pushing "upgrade" at someone mid-cancellation reads
 * tone-deaf, so this offers a way back instead.
 */
export function SubscriptionRetentionCard({ currentPeriodEnd, onManage, loading }: SubscriptionRetentionCardProps) {
  const { t, i18n } = useTranslation('settings');

  const formattedDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div
      data-testid="subscription-retention-card"
      className="rounded-md border border-border bg-muted/40 p-4 space-y-3"
    >
      <div>
        <p className="font-medium">{t('subscription.retention.title')}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formattedDate
            ? t('subscription.retention.text', { date: formattedDate })
            : t('subscription.retention.textNoDate')}
        </p>
      </div>
      <Button onClick={onManage} disabled={loading} className="gap-2">
        {loading && <Spinner size="sm" />}
        {t('subscription.retention.cta')}
      </Button>
    </div>
  );
}
